package fr.frux.core.maps

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.receiveAsFlow

/**
 * Bornes de zoom des controles de zoom des ecrans (boutons +/-).
 *
 * Repli en dur assume : maplibre-compose n'expose aucune lecture des bornes de zoom du style
 * courant, seulement des ecritures (`CameraConstraints`). Elles ne contraignent donc que les
 * deplacements demandes par l'application, pas les gestes : le SDK garde ses propres bornes.
 */
const val MIN_MAP_ZOOM = 1f
const val MAX_MAP_ZOOM = 20f

/** Position de camera : un point vise et un niveau de zoom. */
data class MapCameraPosition(
    val target: Location,
    val zoom: Float,
)

/** Ordre de deplacement, consomme une seule fois par la couche de rendu. */
sealed interface CameraCommand {
    val animate: Boolean

    /** Viser un point a un zoom donne. */
    data class MoveTo(
        val target: MapCameraPosition,
        override val animate: Boolean,
    ) : CameraCommand

    /**
     * Cadrer un rectangle. Le zoom revient a la couche de rendu, seule a connaitre la taille de la
     * vue ; les bords a eviter se lisent dans [MapCameraState.contentPadding] au moment du
     * cadrage, et non ici — la marge n'est mesurable qu'apres la mise en page.
     */
    data class FitBounds(
        val bounds: MapBounds,
        override val animate: Boolean,
    ) : CameraCommand
}

/**
 * Origine d'un deplacement rapporte par la couche de rendu.
 *
 * Distinction necessaire, pas cosmetique : sans elle, une camera deplacee par l'application se
 * lirait comme une prise en main de l'utilisateur, et l'application reagirait a son propre ordre.
 */
enum class CameraMoveSource { GESTURE, PROGRAMMATIC }

/** Dernier deplacement rapporte par la couche de rendu, avec son origine. */
data class CameraMove(
    val position: MapCameraPosition,
    val source: CameraMoveSource,
)

/**
 * Etat de camera partage entre l'application et la couche de rendu.
 *
 * Regle de synchronisation : **la position est un etat, le deplacement est un evenement.**
 *
 * - [position] est un miroir de la camera du SDK. Seul [onGesture] l'ecrit, et il n'emet rien :
 *   c'est ce qui casse la boucle de feedback quand l'utilisateur fait un pan.
 * - Les deplacements passent par un [Channel] conflate, pas par un etat. Un etat serait
 *   re-applique a chaque redemarrage du composable (rotation, recomposition) et ramenerait la
 *   camera la ou l'utilisateur ne veut plus etre.
 */
@Stable
class MapCameraState(
    initial: MapCameraPosition,
) {
    /**
     * Position de depart de la couche de rendu, **hors etat Compose**.
     *
     * La couche de rendu la lit pendant sa composition. Si elle lisait [position] — que
     * [onGesture] ecrit a chaque frame de pan — chaque deplacement invaliderait la composition de
     * la carte entiere (sources, couches, compilation des expressions), au lieu de laisser le SDK
     * redessiner seul.
     */
    val initialPosition: MapCameraPosition = initial

    /**
     * Dernier deplacement rapporte par le SDK. L'application y lit la position *et* son origine :
     * seule la source [CameraMoveSource.GESTURE] traduit une intention de l'utilisateur.
     */
    var lastMove: CameraMove by mutableStateOf(CameraMove(initial, CameraMoveSource.PROGRAMMATIC))
        private set

    val position: MapCameraPosition
        get() = lastMove.position

    private val _commands = Channel<CameraCommand>(Channel.CONFLATED)

    /** Commandes de deplacement. A collecter par la couche de rendu, une seule fois. */
    val commands: Flow<CameraCommand> = _commands.receiveAsFlow()

    /** Demande un deplacement de camera. N'ecrit pas [position]. */
    fun moveTo(
        target: MapCameraPosition,
        animate: Boolean = true,
    ) {
        _commands.trySend(CameraCommand.MoveTo(target, animate))
    }

    /**
     * Bords de la carte couverts par les surcouches de l'ecran (barre de recherche, nav basse).
     *
     * Porte par l'etat plutot que par la commande : une commande capture sa marge a l'emission,
     * or le premier cadrage part avant que la mise en page n'ait mesure quoi que ce soit.
     */
    var contentPadding: PaddingValues by mutableStateOf(PaddingValues(0.dp))

    /**
     * Bords de la carte recouverts par le chrome racine (colonne grand écran, spec 2026-09-10 §5),
     * écrits par la surface persistante et non par l'écran carte, qui n'est plus composé quand une
     * colonne est ouverte. Distinct de [contentPadding] à dessein : celui-ci rejoue le cadrage en
     * attente à chaque changement, alors qu'ouvrir une colonne ne doit déplacer que l'attribution,
     * jamais la caméra.
     */
    var chromeInsets: PaddingValues by mutableStateOf(PaddingValues(0.dp))

    /**
     * Hauteur depuis le bord bas que la feuille de l'ecran carte recouvre (fiche d'arbre, groupe,
     * bandeau d'annonce), pour que l'attribution (ADR 0010) passe AU-DESSUS d'elle. Hors
     * [contentPadding] a dessein : une feuille qui monte ou descend ne doit deplacer que
     * l'attribution, jamais rejouer le cadrage de la camera.
     */
    var bottomOverlayInset: Dp by mutableStateOf(0.dp)

    /** Demande a cadrer [bounds]. Le zoom est calcule par la couche de rendu. */
    fun fitBounds(
        bounds: MapBounds,
        animate: Boolean = true,
    ) {
        _commands.trySend(CameraCommand.FitBounds(bounds, animate))
    }

    /** Appele par la couche de rendu quand le SDK a bouge. */
    fun onCameraMove(
        actual: MapCameraPosition,
        source: CameraMoveSource,
    ) {
        lastMove = CameraMove(actual, source)
    }

    /** Raccourci : le SDK a bouge sous le doigt de l'utilisateur. */
    fun onGesture(actual: MapCameraPosition) {
        onCameraMove(actual, CameraMoveSource.GESTURE)
    }

    companion object {
        val Saver: Saver<MapCameraState, List<Double>> =
            Saver(
                save = {
                    listOf(
                        it.position.target.latitude,
                        it.position.target.longitude,
                        it.position.zoom.toDouble(),
                    )
                },
                restore = {
                    MapCameraState(
                        MapCameraPosition(
                            target = Location(it[0], it[1]),
                            zoom = it[2].toFloat(),
                        ),
                    )
                },
            )
    }
}

/** Cree un [MapCameraState] qui survit a la rotation et a la recomposition. */
@Composable
fun rememberMapCameraState(initial: MapCameraPosition): MapCameraState =
    rememberSaveable(saver = MapCameraState.Saver) { MapCameraState(initial) }
